// Core packages
import { formatDate } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";

// Third party packages
import { MatDialog, MatExpansionPanel, MatSort } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { Workbook } from "exceljs";
import * as FileSaver from "file-saver";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { SearchDocsService } from "src/app/modules/search-docs/search-docs.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import DocumentTypeStructure from "../../../properties/documentTypes";

import { CompanySearchComponent } from "../../components/company-search/company-search.component";
import { SupplierSearchComponent } from "../../components/supplier-search/supplier-search.component";

import { CancelContractDialogComponent } from "src/app/modules/properties/components/cancel-contract-dialog/cancel-contract-dialog.component";
import { PropertyExpireContractItemDialogComponent } from "src/app/modules/properties/components/contract-expire-item-dialog/contract-expire-item-dialog.component";
import { DocumentDownloadDialogComponent } from "src/app/modules/properties/components/document-download-dialog/document-download-dialog.component";
import { PropertyDetailDocumentItemDialogComponent } from "src/app/modules/properties/components/document-item-dialog/document-item-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
interface PropertiesList {
  checked: boolean;
  id: string;
  name: string;
  masterCode?: string;
}
interface Topic {
  checked: boolean;
  code: string;
  name: string;
}

interface Subtopic {
  checked: boolean;
  parent: string;
  type: string;
  code: string;
  name: string;
}

interface Doc {
  checked: boolean;
  visible: boolean;
  parent: string;
  code: string;
  title: string;
  mandatory: boolean;
}

interface Result {
  id: string;
  documentArchiveId: string;
  propertyId: string;
  property: string;
  docType: string;
  structure: string;
  structureId: string;
  content: string;
  reference: string;
  documentDate: Date;
  expirationDate: Date;
  contract: boolean;
  mandatory: boolean;
  expired: boolean;
  privacy?: boolean;
  mockup: boolean;
  data: any;
  pdfmsg?: any;
  cancellationRequestDate?: Date;
  cancellationRequestBy?: string;
  key?: string;
  docId?: string;
  subDocId?: string;
  keyTranslateTitle?: string;
  companyCode?: string;
  propertyCode?: string;
  masterCode?: string;
  barCode?: string;
  loaded?: any;
}

interface SupplierData {
  id: string;
  name: string;
}

@Component({
  selector: "app-search-docs-listv2",
  templateUrl: "./search-docs-listv2.page.html",
  styleUrls: ["./search-docs-listv2.page.scss"],
})
export class SearchDocsListV2Component implements OnInit, OnDestroy {
  @ViewChild("sortTable", { static: true }) sort: MatSort;
  @ViewChild("filterPanel", { static: true }) filterPanel: MatExpansionPanel;
  @ViewChild("supplierSearch", { static: false })
  supplierSearch: SupplierSearchComponent;
  @ViewChild("companySearch", { static: false })
  companySearch: CompanySearchComponent;

  private subscriptions: Subscription[] = [];

  // Logged user
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  loggedUserMaster = false;

  // Section
  step = 1;

  // Company
  companySelected = "";

  // Properties
  propertiesIsLoading = true;
  properties: PropertiesList[] = [];
  displayProperties: PropertiesList[] = [];
  propertySearchValue = "";
  propertyCodeSearchValue = "";
  selectAllProperties = false;
  someSelectedProperties = false;

  // Area
  selectedArea = "";
  areas: any;
  public documentTypeStructure: DocumentTypeStructure =
    new DocumentTypeStructure();
  documentTemplateId = "";
  documentTemplate: any[] = [];

  // Topic
  selectedTopic: any;
  topics: Topic[] = [];
  selectAllTopics = false;
  someSelectedTopics = false;

  // Subtopic
  subtopics: Subtopic[] = [];
  selectAllSubtopics = false;
  someSelectedSubtopics = false;

  // Documents
  documents: Doc[] = [];
  docSearchValue = "";
  displayDocs: Doc[] = [];
  selectAllDocuments = false;
  selectMandatoryDocuments = false;
  condoStructure: any;

  // Supplier
  supplierId = "";
  supplierName = "";

  // Date
  dateFrom = undefined;
  dateTo = undefined;

  // Protocol
  protocolValue = "";

  // Keyword
  keywordValue = "";

  // BEGIN - Result table
  displayedColumns: string[] = [
    "index",
    "property",
    "masterCode",
    "docType",
    "structure",
    "content",
    "reference",
    "documentDate",
    "expirationDate",
    "barCode",
    "actions",
  ];
  results: Result[] = [];
  dataSource: Result[] = [];
  resulIsLoading = false;
  canExport = false;
  showDocumentsExpired = true;
  showDocumentsMandatory = true;
  // END - Result table

  // Show / Edit document
  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public configService: ConfigService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private authService: AuthService,
    private companiesService: CompaniesService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private searchDocsService: SearchDocsService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type,
    private settingsService: SettingsService,
    private documentArchivesService: DocumentArchivesService,
    private suppliersService: SuppliersService,
    public helperService: HelperService,
    public cancelContractDialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });

        if (
          this.currentRole.role.type === "master" ||
          this.currentRole.role.type === "collaborator"
        ) {
          this.loggedUserMaster = true;
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.authService.loggedUserRequestedRoleType$.getValue();
    if (this.currentRole !== "admin" && this.currentRole !== "operator") {
      this.companySelected = this.cookieService.get("requestedCompany");
    }

    this.subscriptions.push(
      this.companiesService
        .shouldShowMasterCodeColumn$(this.currentRole, this.companySelected)
        .subscribe((show) => {
          if (!show) {
            const masterCodeIndex = this.displayedColumns.findIndex((el) => el === "masterCode");
            if (masterCodeIndex > -1) {
              this.displayedColumns.splice(masterCodeIndex, 1);
            }
          }
        })
    );

    this.propertiesIsLoading = true;
    this.subscriptions.push(
      this.searchDocsService.getSearchDocsProperties().subscribe((res) => {
        const items =
          (res.data && res.data.items) ||
          (Array.isArray(res.data) ? res.data : null);
        if (res.status && items && Array.isArray(items)) {
          for (const item of items) {
            this.properties.push({
              checked: false,
              id: item._id,
              name: item.name,
              masterCode: item.masterCode,
            });
          }

          this.displayProperties = JSON.parse(JSON.stringify(this.properties));

          setTimeout(() => {
            this.propertiesIsLoading = false;
            return;
          }, 500);
        } else {
          this.toastrService.error(res.message);

          setTimeout(() => {
            this.propertiesIsLoading = false;
            return;
          }, 500);
        }
      })
    );

    this.subscriptions.push(
      this.settingsService.getDocument().subscribe(
        (res) => {
          if (res.status) {
            if (
              res.data &&
              Array.isArray(res.data) &&
              res.data.length > 0 &&
              res.data[0].area &&
              Array.isArray(res.data[0].area)
            ) {
              this.documentTemplateId = res.data[0]._id;
              this.documentTemplate = res.data[0].area;
            }
          }
        },
        (err) => {
          //
        }
      )
    );

    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => this.sortData())
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle seciont focused
   */
  OnSectionFocused(step: number): void {
    if (!this.propertiesIsLoading && this.someSelectedProperties) {
      this.step = step;
    }
  }

  /**
   * Handle company selected
   */
  OnCompanySelected(event: string): void {
    if (event) {
      this.companySelected = event;

      this.propertiesIsLoading = true;
      this.subscriptions.push(
        this.searchDocsService
          .getSearchDocsProperties(event)
          .subscribe((res) => {
            const items =
              (res.data && res.data.items) ||
              (Array.isArray(res.data) ? res.data : null);
            if (res.status && items && Array.isArray(items)) {
              this.properties = [];
              this.selectAllProperties = false;
              for (const item of items) {
                this.properties.push({
                  checked: false,
                  id: item._id,
                  name: item.name,
                  masterCode: item.masterCode,
                });
              }
              this.displayProperties = JSON.parse(
                JSON.stringify(this.properties)
              );

              setTimeout(() => {
                this.propertiesIsLoading = false;
                return;
              }, 500);
            } else {
              this.toastrService.error(res.message);

              setTimeout(() => {
                this.propertiesIsLoading = false;
                return;
              }, 500);
            }
          })
      );
    }
  }

  /**
   * Handle filter on properties
   *
   * @since 1.0.0
   */
  applyFilterProperties(event: Event): void {
    this.propertySearchValue = (event.target as HTMLInputElement).value;
    this.applyPropertiesFilters();
  }

  /**
   * Handle filter on properties by master code
   *
   * @since 1.0.0
   */
  applyFilterPropertyCode(event: Event): void {
    this.propertyCodeSearchValue = (event.target as HTMLInputElement).value;
    this.applyPropertiesFilters();
  }

  /**
   * Apply the combined name/code filters to the loaded properties list
   *
   * @since 1.0.0
   */
  applyPropertiesFilters(): void {
    const nameNeedle = this.propertySearchValue.trim().toLowerCase();
    const codeNeedle = this.propertyCodeSearchValue.trim().toLowerCase();

    const filteredProperties = this.properties.filter(
      (el: PropertiesList) =>
        (el.name || "").toLowerCase().indexOf(nameNeedle) !== -1 &&
        (el.masterCode || "").toLowerCase().indexOf(codeNeedle) !== -1
    );
    this.displayProperties = JSON.parse(JSON.stringify(filteredProperties));
  }

  /**
   * Handle select all properties
   *
   * @since 1.0.0
   */
  onSelectAllProperties(checked: boolean): void {
    this.selectAllProperties = checked;

    this.displayProperties.forEach(
      (el: PropertiesList) => (el.checked = checked)
    );

    this.someSelectedProperties = checked;
  }

  /**
   * Handle select property
   *
   * @since 1.0.0
   */
  onSelectProperty(): void {
    this.someSelectedProperties = this.displayProperties.some(
      (el: PropertiesList) => el.checked
    );
  }

  /**
   * Get selected area
   *
   * @since 1.0.0
   */
  onChangeArea(): void {
    this.topics = [];
    this.subtopics = [];
    this.docSearchValue = "";
    this.displayDocs = [];
    this.selectAllTopics = false;
    this.selectAllSubtopics = false;
    this.selectAllDocuments = false;
    this.selectMandatoryDocuments = false;

    if (typeof this.selectedArea === "undefined" || this.selectedArea === "") {
      return;
    }

    const subArea = this.documentTemplate.filter((elem: any) => {
      return elem.group === this.selectedArea;
    });
    if (subArea && Array.isArray(subArea)) {
      for (const item of subArea) {
        this.topics.push({
          checked: false,
          code: item.code,
          name: this.lang === "it" ? item.translation.it : item.translation.en,
        });
      }
    }
    this.topics.sort((a: Topic, b: Topic) => {
      if (a.name > b.name) {
        return 1;
      } else if (a.name < b.name) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  /**
   * Handle select topic
   *
   * @since 1.0.0
   */
  onSelectTopic(item: Topic, event: boolean): void {
    if (event) {
      const selectedTopicContent = this.documentTypeStructure.getDocumentTopic2(
        item.code,
        this.documentTemplate
      );
      if (selectedTopicContent && Array.isArray(selectedTopicContent)) {
        for (const el of selectedTopicContent) {
          this.subtopics.push({
            checked: false,
            parent: el.parent,
            type: el.rawTitle,
            code: el.code,
            name: this.lang === "it" ? el.translation.it : el.translation.en,
          });
        }
      }
    } else {
      // remove subtopics and docs
      const subTopic = this.subtopics.find(
        (el: Subtopic) => el.parent === item.code
      );
      this.displayDocs = this.displayDocs.filter(
        (el: Doc) => el.parent !== subTopic.code
      );
      this.documents = this.documents.filter(
        (el: Doc) => el.parent !== subTopic.code
      );

      this.subtopics = this.subtopics.filter(
        (el: Subtopic) => el.parent !== item.code
      );
    }
    this.someSelectedTopics = this.topics.some((el: Topic) => el.checked);

    this.subtopics.sort((a: Subtopic, b: Subtopic) => {
      if (a.name > b.name) {
        return 1;
      } else if (a.name < b.name) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  /**
   * Handle select all topics
   *
   * @since 1.0.0
   */
  onSelectAllTopics(checked: boolean): void {
    this.selectAllTopics = checked;

    this.someSelectedTopics = checked;

    if (!checked) {
      this.subtopics = [];
      this.docSearchValue = "";
      this.displayDocs = [];
      for (const el of this.topics) {
        el.checked = false;
      }
    } else {
      for (const el of this.topics) {
        el.checked = true;
        this.onSelectTopic(el, checked);
      }
    }
  }

  /**
   * Handle select subtopic
   *
   * @since 1.0.0
   */
  onSelectSubtopic(item: Subtopic, event: boolean): void {
    if (event) {
      const documents = this.documentTypeStructure.getDocumentSubTopic2(
        item.code,
        item.parent,
        this.documentTemplate,
        this.condoStructure,
        false,
        this.lang
      );
      if (documents && Array.isArray(documents)) {
        for (const doc of documents) {
          if (!doc.deleted) {
            this.documents.push({
              checked: false,
              visible: doc.visible,
              parent: doc.parent,
              code: doc.code,
              title: doc.title,
              mandatory: doc.mandatory,
            });
          }
        }
      }
    } else {
      this.documents = this.documents.filter((doc) => doc.parent !== item.code);
    }

    this.documents.sort((a: Doc, b: Doc) => {
      if (a.title > b.title) {
        return 1;
      } else if (a.title < b.title) {
        return -1;
      } else {
        return 0;
      }
    });

    this.displayDocs = JSON.parse(JSON.stringify(this.documents));
  }

  /**
   * Handle select all subtopics
   *
   * @since 1.0.0
   */
  onSelectAllSubtopics(checked: boolean): void {
    this.selectAllSubtopics = checked;

    this.someSelectedSubtopics = checked;

    this.documents = [];

    if (!checked) {
      this.docSearchValue = "";
      this.displayDocs = [];
    }
    for (const el of this.subtopics) {
      el.checked = checked;
      this.onSelectSubtopic(el, checked);
    }
  }

  /**
   * Handle select all docs
   *
   * @since 1.0.0
   */
  onSelectAllDocuments(checked: boolean): void {
    this.selectAllDocuments = checked;

    this.documents.forEach((el: Doc) => (el.checked = checked));
    this.displayDocs.forEach((el: Doc) => (el.checked = checked));
  }

  /**
   * Handle select doc
   *
   * @since 1.0.0
   */
  onSelectDoc(item: Doc, checked: boolean): void {
    const doc = this.documents.find((el: Doc) => el.code === item.code);
    if (doc) {
      doc.checked = checked;
    }

    const displayDoc = this.displayDocs.find(
      (el: Doc) => el.code === item.code
    );
    if (displayDoc) {
      displayDoc.checked = checked;
    }
  }

  /**
   * Handle show mandatory docs
   *
   * @since 1.0.0
   */
  onShowMandatoryDocuments(checked: boolean): void {
    this.selectMandatoryDocuments = checked;

    if (checked) {
      const filteredDocs = this.documents.filter((el: Doc) => el.mandatory);
      this.displayDocs = JSON.parse(JSON.stringify(filteredDocs));
    } else {
      this.displayDocs = JSON.parse(JSON.stringify(this.documents));
      this.docSearchValue = "";
    }
  }

  /**
   * Handle filter on properties
   *
   * @since 1.0.0
   */
  applyFilterDocs(event: Event): void {
    this.docSearchValue = (event.target as HTMLInputElement).value;
    const regex = new RegExp(this.docSearchValue.trim().toLowerCase(), "i");
    const filteredDocs = this.documents.filter((el: Doc) => {
      if (
        regex.test(el.title) &&
        (!this.selectMandatoryDocuments ||
          (this.selectMandatoryDocuments && el.mandatory))
      ) {
        return true;
      }
      return false;
    });
    this.displayDocs = JSON.parse(JSON.stringify(filteredDocs));
  }

  /**
   * Handle supplier selected
   */
  OnSupplierSelected(event: SupplierData): void {
    if (event) {
      this.supplierId = event.id;
      this.supplierName = event.name;
    }
  }

  /**
   * Handle reset all filters
   */
  onResetAll(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "searchDocs.dialogs.resetAll.title"
        ),
        message: this.translateService.instant(
          "searchDocs.dialogs.resetAll.text"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.close"),
      },
      width: "500px",
      // disableClose: true
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.someSelectedProperties = false;
        this.propertySearchValue = "";
        this.propertyCodeSearchValue = "";
        this.selectAllProperties = false;
        this.properties = [];

        this.selectedArea = "";
        this.topics = [];
        this.subtopics = [];
        this.docSearchValue = "";
        this.displayDocs = [];
        this.selectAllTopics = false;
        this.selectAllSubtopics = false;
        this.selectAllDocuments = false;
        this.selectMandatoryDocuments = false;

        this.supplierId = "";
        this.supplierName = "";
        this.dateFrom = undefined;
        this.dateTo = undefined;
        this.protocolValue = "";
        this.keywordValue = "";

        this.supplierSearch.onResetSelected();
        if (this.companySearch) {
          this.companySearch.onResetSelected();
        }

        this.propertiesIsLoading = true;
        this.subscriptions.push(
          this.searchDocsService.getSearchDocsProperties().subscribe((res) => {
            const items =
              (res.data && res.data.items) ||
              (Array.isArray(res.data) ? res.data : null);
            if (res.status && items && Array.isArray(items)) {
              for (const item of items) {
                this.properties.push({
                  checked: false,
                  id: item._id,
                  name: item.name,
                  masterCode: item.masterCode,
                });
              }

              this.displayProperties = JSON.parse(
                JSON.stringify(this.properties)
              );

              setTimeout(() => {
                this.propertiesIsLoading = false;
                return;
              }, 500);
            } else {
              this.toastrService.error(res.message);

              setTimeout(() => {
                this.propertiesIsLoading = false;
                return;
              }, 500);
            }
          })
        );

        this.step = 1;
      }
    });
  }

  /**
   * Handle rest filters except for properties
   */
  onReset(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("searchDocs.dialogs.reset.title"),
        message: this.translateService.instant("searchDocs.dialogs.reset.text"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.close"),
      },
      width: "500px",
      // disableClose: true
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.selectedArea = "";
        this.topics = [];
        this.subtopics = [];
        this.docSearchValue = "";
        this.displayDocs = [];
        this.selectAllTopics = false;
        this.selectAllSubtopics = false;
        this.selectAllDocuments = false;
        this.selectMandatoryDocuments = false;

        this.supplierId = "";
        this.supplierName = "";
        this.dateFrom = undefined;
        this.dateTo = undefined;
        this.protocolValue = "";
        this.keywordValue = "";

        this.supplierSearch.onResetSelected();

        this.step = 1;
      }
    });
  }

  /**
   * Handle supplier selected
   */
  onSubmit(): void {
    let properties = [];
    if (
      this.propertySearchValue !== "" ||
      this.propertyCodeSearchValue !== "" ||
      !this.selectAllProperties
    ) {
      properties = this.displayProperties
        .filter((el: PropertiesList) => el.checked)
        .map((el: PropertiesList) => el.id);
    } else {
      properties = ["all"];
    }

    const subtopics = this.subtopics
      .filter((el: Subtopic) => el.checked)
      .map((el: Subtopic) => el.type);
    const docs = this.displayDocs
      .filter((el: Doc) => el.checked)
      .map((el: Doc) => el.code);

    // console.log(subtopics)
    // console.log("this.dateFrom", this.dateFrom)
    // console.log("moment(this.dateFrom).isValid()", moment(this.dateFrom).isValid())

    // Prepare filtering data
    const dataFilter = {
      properties,
      company: this.companySelected,
      area: this.selectedArea,
      subTopics: subtopics,
      documents: docs,
      supplier: this.supplierName,
      startDate:
        this.dateFrom && moment(this.dateFrom).isValid()
          ? moment(this.dateFrom).local(true).toISOString()
          : undefined,
      endDate:
        this.dateTo && moment(this.dateTo).isValid()
          ? moment(this.dateTo).local(true).toISOString()
          : undefined,
      protocollo: this.protocolValue,
      keyword: this.keywordValue,
    };

    // this.showParameters = false;
    this.filterPanel.close();

    this.canExport = false;
    this.resulIsLoading = true;

    this.subscriptions.push(
      this.searchDocsService
        .getSearchDocsGeneralV2(dataFilter)
        .subscribe((res) => {
          if (res.status) {
            this.results = [];

            const items =
              (res.data && res.data.items && Array.isArray(res.data.items))
                ? res.data.items
                : (Array.isArray(res.data) ? res.data : []);
            if (items.length) {
              items.forEach((item: any) => {
                let supplier = "";
                if (item.data && item.data.ragioneSocialeFornitore) {
                  supplier = item.data.ragioneSocialeFornitore;
                } else if (
                  item.data &&
                  item.data.nomeSocietaEroganteInServizio
                ) {
                  supplier = item.data.nomeSocietaEroganteInServizio;
                }
                // this.results.push({
                //   loaded: item.loaded ? item.loaded : "",
                // });
                let barCode = "";
                if (item.data && item.data.barCode) {
                  barCode = `${item.data.barCode.substring(
                    item.data.barCode.length - 5,
                    item.data.barCode.length - 1
                  )} ${item.data.barCode.substring(
                    item.data.barCode.length - 1,
                    item.data.barCode.length
                  )}`;
                }

                this.results.push({
                  id: item._id,
                  documentArchiveId: item.documentArchiveId,
                  propertyId: item.propertyId._id,
                  property: item.propertyId.name,
                  docType: item.type,
                  structure: item.structureObjectName,
                  structureId: item.structureObjectId,
                  content:
                    item.data && item.data.contenutoDocumento
                      ? item.data.contenutoDocumento
                      : "",
                  reference: supplier,
                  documentDate:
                    item.data && item.data.dataDocumento
                      ? item.data.dataDocumento
                      : "",
                  expirationDate: item.dateExpired ? item.dateExpired : "",
                  contract: item.contract,
                  expired: item.expired,
                  privacy: item.privacy,
                  mandatory: item.mandatory,
                  mockup: item.mockup ? true : false,
                  data: item.data,
                  pdfmsg: item.pdfmsg,
                  cancellationRequestDate: item.cancellationRequestDate,
                  cancellationRequestBy: item.cancellationRequestBy,
                  key: item.key,
                  docId: item.structureObjectId !== "" ? item.docId : "",
                  subDocId: item.subDocId,
                  keyTranslateTitle: `properties.documents.${item.subTopic}`,
                  companyCode:
                    item.propertyId &&
                    item.propertyId.admin &&
                    item.propertyId.admin.company &&
                    item.propertyId.admin.company.code
                      ? item.propertyId.admin.company.code
                      : "",
                  propertyCode:
                    item.propertyId && item.propertyId.code
                      ? item.propertyId.code
                      : "",
                  masterCode:
                    item.propertyId && item.propertyId.masterCode
                      ? item.propertyId.masterCode
                      : "",
                  barCode,
                  loaded: item.loaded ? item.loaded : "",
                });
              });
            }

            this.dataSource = JSON.parse(JSON.stringify(this.results));
            this.dataSource.sort((a: Result, b: Result) => {
              if (a.property > b.property) {
                return 1;
              } else if (a.property < b.property) {
                return -1;
              } else {
                return 0;
              }
            });

            if (Array.isArray(this.results) && this.results.length) {
              this.canExport = true;
            }

            this.resulIsLoading = false;
          } else {
            this.resulIsLoading = false;
            this.toastrService.error(res.message);
          }
        })
    );
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortData(): void {
    const data = JSON.parse(JSON.stringify(this.dataSource));
    if (!this.sort.active || this.sort.direction === "") {
      return;
    }

    this.dataSource = data.sort((a: Result, b: Result) => {
      const isAsc = this.sort.direction === "asc";
      switch (this.sort.active) {
        case "property":
          return this.compare(a.property, b.property, isAsc);
        case "masterCode":
          return this.compare(a.masterCode, b.masterCode, isAsc);
        case "docType":
          return this.compare(a.docType, b.docType, isAsc);
        case "content":
          return this.compare(a.content, b.content, isAsc);
        case "structure":
          return this.compare(a.structure, b.structure, isAsc);
        case "reference":
          return this.compare(a.reference, b.reference, isAsc);
        case "documentDate":
          return this.compare(a.documentDate, b.documentDate, isAsc);
        case "dateExpired":
          return this.compare(a.expirationDate, b.expirationDate, isAsc);
        case "barCode":
          return this.compare(a.barCode, b.barCode, isAsc);
        default:
          return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Utility: export results to excel
   *
   * @since 1.0.0
   */
  onExportToExcel(): void {
    let maxPropertyColLength = 0;
    let maxTypeColLength = 0;
    let maxContentColLength = 0;
    let maxReferenceColLength = 0;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Sheet");
    const header = [
      this.translateService.instant("properties.documents.searchDocs.property"),
      this.translateService.instant("properties.documents.searchDocs.type"),
      this.translateService.instant("properties.documents.searchDocs.plant"),
      this.translateService.instant("properties.documents.searchDocs.content"),
      this.translateService.instant(
        "properties.documents.searchDocs.reference"
      ),
      this.translateService.instant("properties.documents.searchDocs.date"),
      this.translateService.instant("properties.documents.searchDocs.expired"),
      this.translateService.instant("properties.documents.searchDocs.loaded"),
      this.translateService.instant("properties.documents.searchDocs.barCode"),
    ];
    worksheet.addRow(header);
    worksheet.lastRow.height = 37.45; // Altezza riga 25
    worksheet.lastRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    worksheet.lastRow.font = {
      size: 7,
    };
    const widths = [30, 40, 40, 40, 30, 12, 12, 18, 18];
    widths.forEach((val, index) => {
      worksheet.getColumn(index + 1).width = val;
    });

    worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
      worksheet.getCell(cell.address).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF87CEEB" },
      };
    });

    // BEGIN - Print settings
    // Set horizontal page orientation
    worksheet.pageSetup = {
      orientation: "landscape",
      paperSize: 9, // A4
      showGridLines: true,
    };

    // Set page header/footer
    worksheet.headerFooter.oddHeader = `&LArchedi&RStampato il ${moment().format(
      "DD/MM/YYYY"
    )}`;
    worksheet.headerFooter.oddFooter = `&L${this.translateService.instant(
      "properties.documents.searchDocs.exportFileName"
    )}&RPage &P/&N`;
    // END - Print settings

    let rowCount = 1;
    this.dataSource.forEach((el) => {
      if (el.property && el.property.length > maxPropertyColLength) {
        maxPropertyColLength = el.property.length + 5;
      }
      if (el.docType && el.docType.length > maxTypeColLength) {
        maxTypeColLength = el.docType.length;
      }
      if (el.content && el.content.length > maxContentColLength) {
        maxContentColLength = el.content.length;
      }
      if (el.reference && el.reference.length > maxReferenceColLength) {
        maxReferenceColLength = el.reference.length + 5;
      }

      rowCount++;

      let type = "";
      if (el.docType) {
        type = el.docType;
        if (el.expired) {
          type = `${type}
            ${this.translateService.instant(
              el.privacy
                ? "searchDocs.resultFields.tagSuperato"
                : "searchDocs.resultFields.tagExpired"
            )}`;
        }
      }

      const row = worksheet.addRow([
        el.property ? el.property : "",
        type,
        el.structure ? el.structure : "",
        el.content ? el.content : "",
        el.reference ? el.reference : "",
        el.documentDate && moment(el.documentDate).isValid()
          ? formatDate(el.documentDate, "dd/MM/yyyy", this.lang)
          : undefined,
        el.expirationDate && moment(el.expirationDate).isValid()
          ? formatDate(el.expirationDate, "dd/MM/yyyy", this.lang)
          : undefined,
        el.loaded && moment(el.loaded).isValid()
          ? formatDate(el.loaded, "dd/MM/yyyy HH:mm:ss", this.lang)
          : undefined,
        el.barCode,
      ]);
      if (el.mandatory && !el.expired && this.helperService.isDocumentDataPopulated(el.data)) {
        // verde
        worksheet.lastRow.font = {
          size: 7,
          color: { argb: "FF28a745" },
        };
      }
      if (el.mandatory && !el.expired && !this.helperService.isDocumentDataPopulated(el.data)) {
        // rosso
        worksheet.lastRow.font = {
          size: 7,
          color: { argb: "FFdc3545" },
        };
      }
      if (!el.mandatory || el.expired) {
        worksheet.lastRow.font = {
          size: 7,
        };
        // nero
      }

      let maxLine = 1;
      for (let i = 1; i <= widths.length; i++) {
        if (i === 1) {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 0,
            vertical: "middle",
            wrapText: true,
          };
        } else {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 1,
            vertical: "middle",
            wrapText: true,
          };
        }

        const cellText = worksheet.lastRow.getCell(i).value;
        let value = -1;
        if (cellText) {
          value =
            Math.ceil(
              worksheet.lastRow.getCell(i).value.toString().length /
                widths[i - 1]
            ) - 1;
          const countLine = (
            worksheet.lastRow
              .getCell(i)
              .value.toString()
              .match(/(?:\r\n|\r|\n)/g) || []
          ).length;
          if (countLine > value) {
            value = countLine;
          }
        }
        if (value > maxLine) {
          maxLine = value;
        }
        worksheet.lastRow.height = 22.45 * maxLine; // 22.45 = altezza riga 15
      }
    });

    // Save to file
    const date = formatDate(new Date(), "yyyyMMddhhmmss", this.lang);
    const fileName = `${this.translateService.instant(
      "properties.documents.searchDocs.exportFileName"
    )} ${date}.xlsx`;
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      FileSaver.saveAs(blob, fileName);
    });

    this.toastrService.success(
      this.translateService.instant(
        "properties.documents.searchDocs.exportedSuccessfully"
      )
    );
  }

  /**
   * Handle click show documents expired
   *
   * @since 1.0.0
   */
  onShowDocsExpired(): void {
    this.showDocumentsExpired = true;

    if (!this.showDocumentsMandatory) {
      this.dataSource = this.results.filter((el: Result) => el.data);
    } else {
      this.dataSource = JSON.parse(JSON.stringify(this.results));
    }
    this.sortData();
  }

  /**
   * Handle click hide documents expired
   *
   * @since 1.0.0
   */
  onHideDocsExpired(): void {
    this.showDocumentsExpired = false;

    if (!this.showDocumentsMandatory) {
      this.dataSource = this.results.filter(
        (el: Result) => !el.expired && el.data
      );
    } else {
      this.dataSource = this.results.filter((el: Result) => !el.expired);
    }
    this.sortData();
  }

  /**
   * Handle click show documents expired
   *
   * @since 1.0.0
   */
  onShowDocsMandatory(): void {
    this.showDocumentsMandatory = true;

    if (!this.showDocumentsExpired) {
      this.dataSource = this.results.filter((el: Result) => !el.expired);
    } else {
      this.dataSource = JSON.parse(JSON.stringify(this.results));
    }
    this.sortData();
  }

  /**
   * Handle click hide documents expired
   *
   * @since 1.0.0
   */
  onHideDocsMandatory(): void {
    this.showDocumentsMandatory = false;

    if (!this.showDocumentsExpired) {
      this.dataSource = this.results.filter(
        (el: Result) => !el.expired && el.data
      );
    } else {
      this.dataSource = this.results.filter((el: Result) => el.data);
    }
    this.sortData();
  }

  /**
   * Handle click on document form to download file
   *
   * @since 1.0.0
   */
  onDownloadDocument(propertyId: string, documents: any): void {
    if (!documents || !documents.length) {
      console.warn("ERROR: NO DOCS");
      return;
    }
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        propertyId: propertyId,
        documents,
      },
      width: "600px",
    });
  }

  /**
   * Handle click on document and open dialog box to edit/show document data
   * and upload document file
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   * action: edit or show doc data
   *
   * @since 1.0.0
   */
  onToggleEditShowDocument(
    documentArchiveId: string,
    modalTitle: string,
    documentKey: string,
    documentId: string,
    subDocumentId: string,
    itemData: any,
    action: string,
    documentFather?: string,
    keyTranslateTitle?: string,
    structureId?: string,
    expired?: boolean,
    contractPdfMsg?: any,
    companyCode?: string,
    propertyCode?: string
  ): void {
    // if (this.subscriptionType === "FREE") {
    //   documentId = "undefined";
    // }
    // Create formly default fields
    const defaultFields = [
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "textarea",
        key: "contenutoDocumento",
        templateOptions: {
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.contenutoDocumento"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.contenutoDocumento"
          ),
          required: true,
          minLength: 2,
          maxLength: 500,
          rows: 5,
          disabled: action === "show" ? true : false,
        },
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "input",
        key: "protocollo",
        templateOptions: {
          readonly: false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          required: false,
          minLength: 0,
          maxLength: 500,
        },
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "textarea",
        key: "keyword",
        templateOptions: {
          readonly: false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          required: false,
          minLength: 0,
          maxLength: 1000,
          rows: 3,
        },
      },
    ];

    // create fields formly from document key: es AA1
    // const aryfields = this.documentTypeStructure.getFormlyFields(
    //   documentKey,
    //   false
    // );
    const schemaType = JSON.parse(
      JSON.stringify(
        this.documentTypeStructure.getDocumentType2(
          documentKey,
          this.documentTemplate
        )
      )
    );
    const aryfields = this.documentTypeStructure.getFormlyFields2(
      schemaType,
      false
    );
    aryfields.forEach((item: any) => {
      if (item.templateOptions && item.templateOptions.placeholder) {
        item.templateOptions.placeholder = this.translateService.instant(
          item.templateOptions.placeholder
        );
      }
      if (item.type === "select") {
        if (Array.isArray(item.templateOptions.options)) {
          item.templateOptions.options.forEach((subItem: any) => {
            subItem.label = this.translateService.instant(subItem.label);
          });
        }
      }
      if (item.type === "selectNew") {
        item.type = "autocomplete";
        if (item.templateOptions.initialValues) {
          const options: any[] = [];
          switch (item.templateOptions.initialValues) {
            case "suppliersName":
              this.suppliersName.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "yearsOfStorage":
              this.yearsOfStorage.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "conductorName":
              this.conductorName.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "nameInterventionArchiving":
              this.nameInterventionArchiving.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
          }
          item.templateOptions.initialValues = options;
          item.templateOptions.addTagText = this.translateService.instant(
            item.templateOptions.addTagText
          );
          item.templateOptions.customLabel = this.translateService.instant(
            item.templateOptions.customLabel
          );
        }
      }
      if (
        item.type === "autocomplete" ||
        item.type === "autocomplete-supplier"
      ) {
        item.templateOptions.addTagText = this.translateService.instant(
          item.templateOptions.addTagText
        );
        item.templateOptions.customLabel = this.translateService.instant(
          item.templateOptions.customLabel
        );
        if (item.templateOptions.search$ === "suppliersService") {
          item.templateOptions.search$ =
            this.suppliersService.getListV2.bind(this);
        }
      } else {
        if (item.templateOptions && item.templateOptions.label) {
          item.templateOptions.label = this.translateService.instant(
            item.templateOptions.label
          );
        }
      }

      if (action === "show" && item.templateOptions) {
        item.templateOptions.disabled = true;
      }
    });

    // const contract = this.documentTypeStructure.isContract(documentKey);
    const contract = this.documentTypeStructure.isContract2(
      documentKey,
      this.documentTemplate
    );

    const fieldGroup = aryfields.concat(defaultFields);

    const itemConfig = [
      {
        fieldGroupClassName: "row",
        fieldGroup: fieldGroup,
      },
    ];

    let barCode = "";
    if (itemData && itemData.barCode) {
      barCode = itemData.barCode;
    }

    // const selectedArea =
    //   this.documentTypeStructure.getAreaFromChild(keyTranslateTitle);
    const selectedArea = this.documentTypeStructure.getAreaFromChild2(
      keyTranslateTitle,
      this.documentTemplate
    );

    let title = "";
    if (keyTranslateTitle) {
      title = keyTranslateTitle.split("properties.documents.")[1];
    }

    const dialogRef = this.dialog.open(
      PropertyDetailDocumentItemDialogComponent,
      {
        data: {
          modalTitle,
          documentArchiveId: documentArchiveId,
          documentTypeRoute: this.documentTypeStructure.getDocRoute2(
            documentKey,
            this.documentTemplate
          ),
          documentId,
          documentKey,
          subDocumentId,
          itemData,
          itemConfig,
          propertyCode,
          companyCode,
          barCode,
          action,
          documentFather,
          keyTranslateTitle: title,
          structureId: structureId !== "" ? structureId : undefined,
          contract,
          expired,
          contractPdfMsg,
          selectedArea,
          property: "",
          companyData: "",
          propertyName: "",
        },
        width: "700px",
      }
    );

    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            // return;
          } else {
            if (res === "reload") {
              this.onSubmit();
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle delete document
   *
   * @since 1.0.0
   */
  onDeleteDocument(item: Result): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "searchDocs.dialogs.deleteDoc.title"
        ),
        message: this.translateService.instant(
          "searchDocs.dialogs.deleteDoc.text",
          {
            doc: item.docType,
            property: item.property,
          }
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.close"),
      },
      width: "500px",
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.resulIsLoading = true;

        const sendData = {
          docs: item.id,
          mockup: item.mockup,
        };
        this.subscriptions.push(
          this.documentArchivesService
            .deleteDocs(item.documentArchiveId, sendData)
            // .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);

                  this.results = this.results.filter((el: Result) => el.id.toString() !== item.id.toString());
                  this.dataSource = this.dataSource.filter((el: Result) => el.id.toString() !== item.id.toString());

                  this.resulIsLoading = false;
                }
              },
              (err) => {
                this.toastrService.error(this.translateService.instant(err));
                this.resulIsLoading = false;
              }
            )
        );
      }
    });
  }

  /**
   * Handle click on document form to cancel contract
   *
   * @since 1.0.0
   */
  onCancelContractDialogOpen(
    documentArchiveId: string,
    propertyName: string,
    propertyId: string,
    modalTitle: string,
    itemData: any
  ): void {
    const contract = {
      ...itemData,
      documentArchiveId: documentArchiveId,
      title: modalTitle,
      companyData: "",
      propertyName: propertyName,
      propertyId: propertyId,
    };

    // Open dialog
    const dialogRef = this.cancelContractDialog.open(
      CancelContractDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          contract,
          subject: this.translateService.instant(
            "communications.fields.cancelContractSubject"
          ),
          body: this.translateService.instant(
            "communications.fields.cancelContractImmediateBody",
            { title: modalTitle.trim() }
          ),
        },
        disableClose: true,
      }
    );

    // On dialog closing, let's update model
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(
        (res) => {
          if (res) {
            const sendData = {
              msg: res,
            };
            this.subscriptions.push(
              this.documentArchivesService
                .cancelContract(documentArchiveId, sendData)
                .pipe(take(1))
                .subscribe(
                  (res) => {
                    console.log(res);
                    if (!res) {
                      return;
                    } else {
                      this.onSubmit();
                    }
                  },
                  (err) => {
                    this.helperService.handleError(err);
                  }
                )
            );
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Handle click on document form to expire contract
   *
   * @since 1.0.0
   */
  onHandleExpiredContract(documentArchiveId: string, itemData: any): void {
    const dialogRef = this.dialog.open(
      PropertyExpireContractItemDialogComponent,
      {
        data: {
          documentArchiveId: documentArchiveId,
          barCode: itemData.barCode ? itemData.barCode : "",
        },
        width: "600px",
      }
    );
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            // @TODO aggironare la tabella
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }
}
